import { Box, Grid, Typography, List, ListItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PrimaryBlueButton from '../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../components/common/buttons/BorderedButton';
import { styled } from '@mui/material/styles';
import OrderCheckoutProductImage from './OrderCheckoutProductImage';
import { useEffect, useState } from 'react';
import { getProduct, getProductPrice, getProductCompareAtPrice } from './utils';
import { DEFAULT_PRODUCT_HANDLE } from '../constants/shop';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

const GridContainerStepOne = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    flexDirection: 'column',
  },
}));

type OrderCheckoutFirstStepProps = {
  selectQuantityAndHandleNext: () => void;
};

export default function OrderCheckoutFirstStep({ selectQuantityAndHandleNext }: OrderCheckoutFirstStepProps) {
  const { theme } = useThemeContext();
  const navigate = useNavigate();
  const [productPrice, setProductPrice] = useState<string | null>(null);
  const [productCompareAtPrice, setProductCompareAtPrice] = useState<string | null>(null);

  useEffect(() => {
    const fetchProductPrice = async () => {
      const sProduct = await getProduct(DEFAULT_PRODUCT_HANDLE);
      const sProductPrice = getProductPrice(sProduct);
      const sProductCompareAtPrice = getProductCompareAtPrice(sProduct);
      setProductPrice(sProductPrice);
      setProductCompareAtPrice(sProductCompareAtPrice);
    };
    fetchProductPrice();
  }, []);

  return (
    <>
      <GridContainerStepOne container spacing={0}
        sx={{
          margin: 0,
          width: '100%'
        }}
      >
        <OrderCheckoutProductImage hideForMobile={false} />
        <Grid item xs={12} sm={12} md={7} sx={{ paddingLeft: { md: '30px !important' } }}>
          <Typography gutterBottom sx={{ typography: { xs: 'h4', md: 'h2' } }}>
            Buy home PFAS test
          </Typography>
          <Typography gutterBottom sx={{ fontWeight: 'bold', typography: { sm: 'h6', md: 'h5' } }}>
            You are buying a PFAS test kit
          </Typography>
          <Typography variant="body1">
            <span style={{textDecoration: 'line-through'}}>{productCompareAtPrice}</span>
            {productCompareAtPrice && ' '}
            <span style={{fontWeight: 'bold'}}>{productPrice}</span>&nbsp;
            per test kit.</Typography>
          <Typography variant="body1" sx={{ color: theme.custom.orderPFAS.first.shippingTextColor }}>
            Free shipping
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: { xs: 'center', md: 'flex-start' },
              maxWidth: { xs: '100%', md: 'auto' },
            }}
          >
            <PrimaryBlueButton
              onClick={selectQuantityAndHandleNext}
              buttonText="Next"
              sxProps={{
                minWidth: { 'md': '120px' }
              }}/>
            <BorderedButton
              sxProps={{
                height: { xs: '41px' },
                marginTop: { xs: '10px', md: '0' },
                marginLeft: { xs: '0', md: '10px' },
                minWidth: { 'md': '120px' }
              }}
              onClick={() => navigate('/')}
              buttonText="Cancel"
            />
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: { xs: 'center', md: 'flex-start' },
              maxWidth: { xs: '100%', md: 'auto' },
            }}
          >
            <List sx={{ listStyleType: 'disc' }}>
              <ListItem sx={{ display: 'list-item', paddingLeft: '0px', marginLeft: '15px' }}>
                Panel of PFAS Compounds: Tests for 15 different PFAS compounds, including those that are most commonly
                 found in people and can cause harm, such as PFOA, PFOS, PFHxS, and others.
              </ListItem>
              <ListItem sx={{ display: 'list-item', paddingLeft: '0px', marginLeft: '15px' }}>
                Independently Validated: The PFAS Core Panel test is independently validated, and our central lab is
                 registered in the State of Nevada and CLIA certified by CMS.
              </ListItem>
              <ListItem sx={{ display: 'list-item', paddingLeft: '0px', marginLeft: '15px' }}>
                User-Friendly Results: Get a detailed report showing concentrations of each compound (if detected),
                 how you compare to other results, and get recommendations for reducing PFAS and improving your wellness.
              </ListItem>
              <ListItem sx={{ display: 'list-item', paddingLeft: '0px', marginLeft: '15px' }}>
                At-Home Testing: Conveniently take the test at home and ship back to our lab for quick results.
                 No need for a visit to the doctor's office or lab.
              </ListItem>
              <ListItem sx={{ display: 'list-item', paddingLeft: '0px', marginLeft: '15px' }}>
                Simple, All-Inclusive Kit: Receive the test kit with everything you need, including a precise sampling
                 device, sample taking supplies, shipping envelope, and easy-to-follow video instructions.
              </ListItem>
            </List>
          </Box>
        </Grid>
      </GridContainerStepOne>
    </>
  );
}
